import {ReactElement, useState, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'

import {CopyIcon, RenderIf} from 'components'
import Logger from 'services/Logger'

const AnimatedCheckmarkIcon = ({
  width = 16,
  height = 16,
}: {
  width?: number
  height?: number
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="check-icon-wrapper"
  >
    <path className="checkmark-path" d="M20 6L9 17L4 12" />
  </svg>
)

export enum TooltipPosition {
  top = 'top',
  right = 'right',
  bottom = 'bottom',
}

interface AnimatedCopyButtonProps {
  onCopy?: (value: string) => void
  value?: string
  className?: string
  isCopied?: boolean
  tooltipPosition?: TooltipPosition
  ariaLabel?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'subtle' | 'primary'
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  tooltipContent?: string
  dataCy?: string
  iconClasses?: string
  disabled?: boolean
  /** Colour of the copied feedback: the product green, or neutral white on
   *  dark surfaces where green would be the only saturated colour. */
  tone?: 'success' | 'neutral'
}

const FEEDBACK_TONE_CLASSES = {
  success: {
    check: 'text-[#00d37e]',
    tooltip: 'bg-[rgba(0,211,126,0.2)] text-[#00d37e]',
  },
  // The neutral pill also sits 1rem further right: on Dopamine's answer card
  // the button is at the card's left edge, and the right-anchored pill would
  // otherwise poke out of it by about 11px.
  neutral: {
    check: 'rounded bg-white/10 text-white',
    tooltip:
      'bg-dopamine-header text-white border-0.5 border-white/10 shadow-lg translate-x-4',
  },
} as const

export function AnimatedCopyButton({
  onCopy,
  value,
  className = '',
  isCopied = false,
  tooltipPosition = TooltipPosition.top,
  ariaLabel,
  size = 'md',
  variant = 'default',
  onClick,
  tooltipContent,
  dataCy,
  iconClasses,
  disabled = false,
  tone = 'success',
}: AnimatedCopyButtonProps): ReactElement {
  const toneClasses = FEEDBACK_TONE_CLASSES[tone]
  const [showCheckmark, setShowCheckmark] = useState(false)
  const {t} = useTranslation()
  const timer = useRef<NodeJS.Timeout>()
  const resolvedLabel =
    tooltipContent ||
    ariaLabel ||
    t('common:COPY_TO_CLIPBOARD', 'Copy to clipboard')

  useEffect(() => {
    if (isCopied) {
      setShowCheckmark(true)
    }
  }, [isCopied])

  useEffect(() => {
    if (showCheckmark) {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        setShowCheckmark(false)
      }, 1000)
    }
    return () => clearTimeout(timer.current)
  }, [showCheckmark])

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()

    if (onClick) {
      onClick(e as React.MouseEvent<HTMLButtonElement>)
      return
    }

    if (onCopy && value) {
      onCopy(value)
      setShowCheckmark(true)
      return
    }

    if (!value) return

    try {
      await navigator.clipboard.writeText(value)
      setShowCheckmark(true)
    } catch (error) {
      Logger.LogNonAxiosError(error)
    }
  }

  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  const variantClasses = {
    default: 'opacity-70 hover:opacity-100',
    subtle: 'opacity-50 hover:opacity-80',
    primary: 'opacity-90 hover:opacity-100',
  }

  if (disabled) {
    return (
      <button
        className={classNames(className, 'pointer-events-none opacity-50')}
        disabled={true}
        data-cy={dataCy}
        aria-label={resolvedLabel}
      >
        <CopyIcon classes={iconClasses || sizeClasses[size]} />
      </button>
    )
  }

  const tooltipClasses = {
    top: classNames(
      '-right-4 -top-9',
      showCheckmark
        ? 'transform translate-y-0 scale-100'
        : 'transform translate-y-1.5 scale-80',
    ),
    right: classNames(
      'left-[calc(100%+8px)] top-1/2',
      showCheckmark
        ? 'transform -translate-y-1/2 scale-100'
        : 'transform -translate-y-1/2 -translate-x-2.5 scale-80',
    ),
    bottom: classNames(
      '-right-4 top-[calc(100%+8px)]',
      showCheckmark
        ? 'transform translate-y-0 scale-100'
        : 'transform -translate-y-1.5 scale-80',
    ),
  }

  return (
    <div
      className={classNames(
        'flex items-center justify-center cursor-pointer transition-all duration-200 ease-in-out relative ml-2',
        variantClasses[variant],
        'hover:opacity-100',
        '[&>svg]:scale-120 [&>svg]:transition-transform [&>svg]:duration-200 [&>svg]:ease-in-out',
        'hover:[&>svg]:scale-130',
        className,
      )}
    >
      <button
        type="button"
        onClick={handleCopy}
        className={classNames('bg-none border-none p-0', className)}
        data-cy={dataCy}
        aria-label={resolvedLabel}
      >
        <div
          className={classNames(
            'transition-opacity duration-200 ease-in-out',
            showCheckmark ? 'opacity-0' : 'opacity-100',
          )}
        >
          <CopyIcon classes={iconClasses || sizeClasses[size]} />
        </div>
      </button>

      <RenderIf isTrue={showCheckmark}>
        <div
          className={classNames(
            'absolute top-0 left-0 w-full h-full flex items-center justify-center',
            toneClasses.check,
          )}
          data-cy="copy-feedback-check"
        >
          <AnimatedCheckmarkIcon width={16} height={16} />
        </div>
      </RenderIf>

      <div
        aria-hidden="true"
        className={classNames(
          'absolute px-2 py-1 rounded text-xs font-semibold whitespace-nowrap pointer-events-none transition-all duration-200 ease-in-out z-10',
          toneClasses.tooltip,
          tooltipClasses[tooltipPosition],
          showCheckmark ? 'opacity-100' : 'opacity-0',
        )}
      >
        {t('common:COPIED', 'Copied!')}
      </div>
      <RenderIf isTrue={showCheckmark}>
        <span aria-live="polite" className="sr-only" role="status">
          {t('common:COPIED', 'Copied!')}
        </span>
      </RenderIf>
    </div>
  )
}

export default AnimatedCopyButton
